/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {type FC, useCallback, useEffect, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import type {CreateFlowFeatureControllerToggleProps} from '~/CreateFabricInvitation/types/types';
import {FEATURE_QA_IDS} from '~/CreateFabricInvitation/utils/constants';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {WtbOverlay} from '~/common/fabric/CreateDesign/components/WhatToBring/WhatToBringOverlay';
import {CREATE_EDIT_WTB} from '~/common/fabric/reducers/widgets';
import {selectHasWtb} from '~/common/fabric/reducers/widgets/selectors';
import {EventFeature} from '~/common/types/event';
import {pluralize} from '~/common/utils';
import {WhatToBringLineIcon} from '~library/svg/WhatToBringLineIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';

import {aquamarine} from '~libSass/v2/_exports.sass';

export const WhatToBringToggle: FC<CreateFlowFeatureControllerToggleProps> = ({
  active,
  renderAs = 'block',
  hide = false,
}) => {
  const store = useStore();
  const hasWtb = useSelector(selectHasWtb);

  const [listenForChanges, setListenForChanges] = useState(false);
  const [wtbSwitchOn, setWtbSwitchOn] = useState(hasWtb);
  const [showWtbOverlay, setShowWtbOverlay] = useState(false);
  const eventWidgets = useSelector(({widgets}: {widgets: any}) => widgets);
  const [itemCount, setItemCount] = useState(
    eventWidgets?.whatToBrings ? eventWidgets?.whatToBrings[0]?.items.length : 0
  );
  const subtitle = hasWtb
    ? `${pluralize('item', itemCount)} added`
    : 'Let guests know what to contribute.';

  const clearWtbData = () => {
    store.dispatch({
      type: CREATE_EDIT_WTB,
      payload: {whatToBrings: []},
    });
  };

  const resetWtbState = () => {
    clearWtbData();
    setShowWtbOverlay(false);
    setListenForChanges(true);
  };

  const handlePanelClose = useCallback((wtb?: any) => {
    if (!wtb) {
      setWtbSwitchOn(false);
    }
    setListenForChanges(false);
    setShowWtbOverlay(false);
  }, []);

  const overlayCallback = useCallback((wtbWidget?: any) => {
    setWtbSwitchOn(true);
    setItemCount(wtbWidget?.items?.length);
  }, []);

  const handleOnClick = useCallback(() => {
    if (!wtbSwitchOn) {
      setWtbSwitchOn(true);
    }
    if (hasWtb) scrollElementIntoViewById('module-what_to_bring', 'center', 0);
    setShowWtbOverlay(true);
  }, [wtbSwitchOn, hasWtb]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    // TODO: fix issue (panel always open) that requires us to omit some deps
    if (listenForChanges) {
      if (!hasWtb) {
        setShowWtbOverlay(true);
      } else {
        resetWtbState();
      }
    } else if (!hasWtb) {
      setListenForChanges(true);
    } else if (!wtbSwitchOn) {
      resetWtbState();
    }
  }, [wtbSwitchOn]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!hasWtb) {
      resetWtbState();
    } else {
      handlePanelClose(!!eventWidgets?.whatToBrings[0]);
    }
  }, [hasWtb]);

  return (
    <InvitationWidget.Toggle
      editable={hasWtb}
      filled={active}
      title="What to Bring List"
      subtitle={subtitle}
      qaId={FEATURE_QA_IDS[EventFeature.WHAT_TO_BRING] as string}
      onClick={handleOnClick}
      renderAs={renderAs}
      hide={hide}
      startAdornment={<WhatToBringLineIcon ratio={0.75} color={aquamarine} />}
      overlay={
        <WtbOverlay
          isOpen={showWtbOverlay}
          onClose={handlePanelClose}
          overlayCallback={overlayCallback}
          title="What to Bring List"
          body="Tell guests exactly what you want them to bring to your event. (PS: you can keep updating this after you've sent your Invitation.)"
          hasWtb={hasWtb}
        />
      }
    />
  );
};
